// @flow
import React from 'react';
import styled from 'styled-components';
import type { SlideshowData } from 'api/shared';
import { imageUrl } from 'utils/filestack';
import Text from 'components/Text';
import Carousel, { Slide } from 'containers/Carousel';
import Box from 'components/Box';
import Video from 'components/Video';
import Hide from 'components/Hide';
import ArrowNavigation from 'components/ArrowNavigation';
import Button, { TextButton } from 'components/Button';
import { ImageSpinner } from 'components/Image';
import ShowModalContainer from 'containers/ShowModalContainer';
import type { ModalType } from 'containers/ModalRoot';
import { hiDPI } from 'utils/style';
import { colors, breakpoints } from '../theme';

const getImageUrl = (
  handle: string,
  width: number,
  quality: number,
  mimetype: string
) =>
  imageUrl({
    handle,
    mimetype,
    transform: `resize=width:${width},height:${width},fit:crop/quality=value:${quality}/output=format:jpg`
  });

const SlideshowImage = styled(
  ({ isVideo, item, index, round, fullWidth, visible, ...props }) => (
    <Button aria-label={getText('Open gallery')} {...props} />
  )
)`
  position: relative;
  transform: translateZ(0);
  width: 100%;
  height: 75vw;
  max-height: 360px;
  border: 0;
  appearance: none;
  background-size: cover;
  background-position: center;
  border-radius: ${({ round }) => (round ? '40px' : 0)};
  ${props =>
    !props.fullWidth &&
    `
    display: block;
    max-width: 360px;
    margin: 0 auto;
  `}
  ${props =>
    props.visible &&
    props.item &&
    !props.isVideo &&
    `
      background-image: url('${getImageUrl(
        props.item.handle,
        props.fullWidth ? 640 : 360,
        90,
        props.item.mimetype
      )}');
      ${hiDPI()} {
        background-image: url('${getImageUrl(
          props.item.handle,
          props.fullWidth ? 1280 : 720,
          50,
          props.item.mimetype
        )}');
      }
    `}
`;

const SlideshowAccessibleButton = styled(Button)`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: calc(100% - 36px);
  appearance: none;
  border: 0;
  background: transparent;
  pointer-events: none;
  @media (min-width: ${breakpoints[2]}) {
    height: ${props => (props.$navShown ? 'calc(100% - 42px)' : '100%')};
  }
`;

const SlideshowContainer = styled(
  ({
    items,
    className,
    showModal,
    fullWidth,
    round,
    qaId = 'carousel'
  }: {
    items: Array<SlideshowData>,
    className: string,
    showModal: (ModalType, ?Object) => void,
    fullWidth?: boolean,
    round?: boolean,
    qaId?: string
  }) => (
    <Box className={className}>
      <SlideshowAccessibleButton
        aria-label={getText('Open gallery')}
        title="Open image gallery"
        $navShown={items.length > 3}
        onClick={e => {
          e.preventDefault();
          showModal('MEDIA_GALLERY', {
            items,
            itemIndex: 0
          });
        }}
      />
      <Carousel
        data-qa-id={qaId}
        pageLength={[
          1,
          1,
          1,
          Math.min(items.length, 3),
          Math.min(items.length, 3)
        ]}
        gutter={round ? 40 : undefined}
        navigation={[
          ({
            current,
            total,
            nextPage,
            prevPage
          }: {
            current: number,
            total: number,
            nextPage: Function,
            prevPage: Function
          }) => {
            const prevDisabled = current <= 0;
            const nextDisabled = current >= total - 1;
            return (
              <>
                {total > 1 && (
                  <>
                    <Hide md={fullWidth} lg={fullWidth} xl={fullWidth}>
                      <ArrowNavigation
                        nextPage={nextPage}
                        prevPage={prevPage}
                        current={current}
                        total={total}
                        outset
                        mt={-18}
                      />
                      <Box classes={['flex', 'justify-center']} mt="24px">
                        <Text
                          color={colors.battleshipGrey}
                          lineHeight="0.8"
                          fontSize="14px"
                          letterSpacing="0.44px"
                        >
                          {current + 1} of {total}
                        </Text>
                      </Box>
                    </Hide>
                    {fullWidth && (
                      <Hide xs sm>
                        <Box
                          classes={['flex', 'align-center', 'justify-center']}
                          mt="24px"
                        >
                          <TextButton
                            onClick={prevPage}
                            disabled={prevDisabled}
                            data-qa-id="prev-media"
                          >
                            {getText('Previous')}
                          </TextButton>
                          <Text
                            color={colors.battleshipGrey}
                            m="0 10px"
                            p="0 10px"
                            borderLeft={`1px solid ${colors.silver}`}
                            borderRight={`1px solid ${colors.silver}`}
                            lineHeight="0.8"
                            fontSize="14px"
                            letterSpacing="0.44px"
                          >
                            {current + 1} of {total}
                          </Text>
                          <TextButton
                            onClick={nextPage}
                            disabled={nextDisabled}
                            data-qa-id="next-media"
                          >
                            {getText('Next')}
                          </TextButton>
                        </Box>
                      </Hide>
                    )}
                  </>
                )}
              </>
            );
          }
        ]}
        slides={({ lastPageLoaded, pageLength }) =>
          items &&
          items.map((item, index) => (
            <Slide
              data-qa-id={`${qaId}-slider-slide-${index}`}
              key={item.handle || item.videoUrl}
              position="relative"
              borderRadius={round ? '40px' : 0}
              bg={colors.fog}
            >
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <ImageSpinner />
              </Box>
              <SlideshowImage
                data-qa-id={`${qaId}-image`}
                item={item}
                index={index}
                tabIndex="-1"
                onClick={e => {
                  e.preventDefault();
                  showModal('MEDIA_GALLERY', {
                    items,
                    itemIndex: index
                  });
                }}
                isVideo={item.type === 'VIDEO'}
                round={round}
                fullWidth={fullWidth}
                visible={(lastPageLoaded + 1) * pageLength > index}
              >
                {item.type === 'VIDEO' && item.videoUrl && (
                  <Video url={item.videoUrl} prefix={qaId} fill noPlayer />
                )}
              </SlideshowImage>
            </Slide>
          ))
        }
      />
    </Box>
  )
)`
  position: relative;
`;

const Slideshow = ({
  items,
  fullWidth,
  round,
  'data-qa-id': qaId
}: {
  items: Array<SlideshowData>,
  fullWidth?: boolean,
  round?: boolean,
  'data-qa-id'?: string
}) => (
  <ShowModalContainer
    render={showModal => (
      <SlideshowContainer
        items={items}
        showModal={showModal}
        fullWidth={fullWidth}
        round={round}
        qaId={qaId}
      />
    )}
  />
);

export default Slideshow;
